import { useCallback, useMemo } from 'react';

import { deleteUserFiles } from '@webapp/account/src/resources/survey';

import type { IConfirmModalHook } from '@webapp/common/components/popups/confirm';
import { ConfirmRemovePopup } from '@webapp/common/components/popups/confirm-remove-popup';
import { useActions } from '@webapp/common/hooks/use-actions';
import { useToggle } from '@webapp/common/hooks/use-toggle';

import css from './confirm-common.css';

const ConfirmFileDelete: FC<{
    onClose: () => void;
    visible: boolean;
    questionId?: number;
    surveyId?: number;
    withFilters: boolean;
    audio?: boolean;
}> = ({ audio, onClose, questionId, surveyId, visible, withFilters }) => {
    const deleteFiles = useActions(deleteUserFiles);

    const onClickDeleteFiles = useCallback(() => {
        if (questionId) {
            deleteFiles({ surveyId: null, questionId, withFilters });
        } else if (surveyId) {
            deleteFiles({ surveyId, questionId: null, withFilters, audio });
        }
    }, [questionId, surveyId, deleteFiles, withFilters, audio]);

    return (
        <ConfirmRemovePopup
            title='Удалить файлы'
            visible={visible}
            onCancel={onClose}
            onClose={onClose}
            onPurge={onClickDeleteFiles}
        >
            <div className={css.center}>
                <span>Вы действительно хотите удалить файлы?</span>
                <br />
                {withFilters && (
                    <span>
                        (с учетом фильтров)
                        <br />
                    </span>
                )}
                <span>Восстановление невозможно!</span>
            </div>
        </ConfirmRemovePopup>
    );
};

export const useConfirmDeleteFiles = (
    questionId: number,
    surveyId: number,
    withFilters: boolean,
    audio?: boolean,
): IConfirmModalHook => {
    const { enabled: visible, off: hide, on: show } = useToggle(false);

    return useMemo(
        () => ({
            show,
            hide,
            // eslint-disable-next-line react/jsx-key
            element: (
                <ConfirmFileDelete
                    audio={audio}
                    questionId={questionId}
                    surveyId={surveyId}
                    visible={visible}
                    withFilters={withFilters}
                    onClose={hide}
                />
            ),
        }),
        [audio, hide, questionId, show, surveyId, visible, withFilters],
    );
};
